import generated from './hero-content.generated.json';
import i18nFragment from './i18n.generated.json';
import type {
  BundleDescriptor,
  CompiledBundle,
  CompiledCollectible,
  CompiledCosmetic,
  CompiledHero,
  CompiledTheme,
  HeroContent,
} from './types';

/**
 * The two flat-collectible families (ADR-0114) as narrowed views of {@link CompiledCollectible} — honest type
 * narrowings (not runtime shims) so the API's per-kind reads keep meaningful names. Pet and background are the
 * same shape; the `kind` tag is the only structural difference.
 */
export type CompiledPet = CompiledCollectible & { readonly kind: 'pet' };
export type CompiledBackground = CompiledCollectible & { readonly kind: 'background' };

/**
 * The compiled hero/cosmetic bundle catalog (ADR-0097) — the single source of truth both apps consume.
 * Built from `content/<bundle>/bundle.yaml` by `scripts/compile.ts` and committed as
 * `hero-content.generated.json` so there is no YAML parsing at API build or runtime. CI's `compile --check`
 * gate fails if the committed JSON drifts from the bundles.
 */
export const HERO_CONTENT: HeroContent = generated as unknown as HeroContent;

/**
 * The CO-LOCATED i18n fragment (ADR-0110 §3) — the per-hero `strings.yaml` translations compiled to the
 * stable runtime keys, nested by `{ <locale>: { <namespace>: { …keys… } } }`. `packages/i18n` DEEP-MERGES this
 * into its catalogs so hero/cosmetic strings live beside the content, never hand-maintained in two places.
 * Pure JSON (no React) — safe for any consumer of the data root.
 */
export type HeroI18nFragment = Record<
  'en' | 'uk',
  { readonly play: Record<string, unknown>; readonly common: Record<string, unknown> }
>;
export const HERO_I18N_FRAGMENT: HeroI18nFragment = i18nFragment as unknown as HeroI18nFragment;

/** Every bundle (the per-household enablement units), in catalog order. */
export const BUNDLES: readonly CompiledBundle[] = HERO_CONTENT.bundles;

/** Every hero (from hero bundles), in catalog order — the API seeds play_content archetype rows from these. */
export const HEROES: readonly CompiledHero[] = HERO_CONTENT.heroes;

/**
 * The STARTER heroes (ADR-0127) — every tier-`0` (free) hero (royal + prince), owned by EVERY child from day
 * one: never unlocked, never charged. The app layer treats these as implicitly owned so a kid always has ≥ 2
 * heroes to switch between from the first session, without materializing a play_state row. Derived, so
 * re-tiering a hero to/from `0` moves it in/out of the starter set with no code change.
 */
export const STARTER_ARCHETYPES: readonly string[] = HEROES.filter((h) => h.tier === 0).map(
  (h) => h.archetypeId,
);

/** Whether an archetype is a free day-one starter (implicitly owned by every child) — ADR-0127. */
export function isStarterHero(archetypeId: string): boolean {
  return STARTER_ARCHETYPES.includes(archetypeId);
}

/** Look up a compiled hero by its archetype id (the play_content slug), or undefined. */
export function heroByArchetype(archetypeId: string): CompiledHero | undefined {
  return HEROES.find((h) => h.archetypeId === archetypeId);
}

/** Every cosmetic (1-1 hero-bound), in catalog order — the API seeds cosmetic_item rows from these. */
export const HERO_COSMETICS: readonly CompiledCosmetic[] = HERO_CONTENT.cosmetics;

/** Every cross-hero flat collectible (ADR-0114) — pets + backgrounds in one kind-tagged array, catalog order. */
const COLLECTIBLES: readonly CompiledCollectible[] = HERO_CONTENT.collectibles ?? [];

/** Every cross-hero PET (ADR-0108), in catalog order — the API's list-pets query reads this derived view (no seed). */
export const PETS: readonly CompiledPet[] = COLLECTIBLES.filter(
  (c): c is CompiledPet => c.kind === 'pet',
);

const PETS_BY_SLUG: ReadonlyMap<string, CompiledPet> = new Map(PETS.map((p) => [p.slug, p]));

/** Look up a compiled pet by its immutable slug (the per-child ownership key + art identity), or undefined. */
export function petBySlug(slug: string): CompiledPet | undefined {
  return PETS_BY_SLUG.get(slug);
}

/** Every cross-hero BACKGROUND (ADR-0110 §7), in catalog order — the API's list-backgrounds query reads this derived view (no seed). */
export const BACKGROUNDS: readonly CompiledBackground[] = COLLECTIBLES.filter(
  (c): c is CompiledBackground => c.kind === 'background',
);

const BACKGROUNDS_BY_SLUG: ReadonlyMap<string, CompiledBackground> = new Map(
  BACKGROUNDS.map((b) => [b.slug, b]),
);

/** Look up a compiled background by its immutable slug (the per-child ownership key + art identity), or undefined. */
export function backgroundBySlug(slug: string): CompiledBackground | undefined {
  return BACKGROUNDS_BY_SLUG.get(slug);
}

/** Every browse THEME (ADR-0113 Tier 1.3 registry), in canonical (sortOrder) order — consumed web-side. */
export const THEMES: readonly CompiledTheme[] = HERO_CONTENT.themes ?? [];

/** The themes of one collectible type, in canonical order (e.g. `themesOfType('pet')`). */
export function themesOfType(type: CompiledTheme['type']): CompiledTheme[] {
  return THEMES.filter((t) => t.type === type);
}

/** Thin descriptors for per-household enablement validation (the ≥1-hero invariant) — no payload. */
export const BUNDLE_DESCRIPTORS: readonly BundleDescriptor[] = HERO_CONTENT.bundles.map((b) => ({
  bundleSlug: b.bundleSlug,
  kind: b.kind,
  isDefault: b.isDefault,
  status: b.status,
}));

/** The bundle slugs that must always be available by default (every isDefault hero bundle). */
export const DEFAULT_BUNDLE_SLUGS: readonly string[] = HERO_CONTENT.bundles
  .filter((b) => b.isDefault)
  .map((b) => b.bundleSlug);

const COSMETICS_BY_SLUG: ReadonlyMap<string, CompiledCosmetic> = new Map(
  HERO_CONTENT.cosmetics.map((c) => [c.slug, c]),
);

/** Look up a compiled cosmetic by its immutable slug (the cross-context identity), or undefined. */
export function cosmeticBySlug(slug: string): CompiledCosmetic | undefined {
  return COSMETICS_BY_SLUG.get(slug);
}

export {
  COINS_PER_EFFORT_DAY,
  coinCostForEffort,
  collectibleEffortDays,
  heroEffortDays,
} from './pricing';
export { COSMETIC_CATEGORIES, CONTENT_STATUSES, COLLECTIBLE_KINDS } from './schema';
export type {
  BundleDescriptor,
  BundleKind,
  CollectibleKind,
  CompiledBundle,
  CompiledCollectible,
  CompiledCosmetic,
  CompiledHero,
  CompiledTheme,
  ContentStatus,
  HeroContent,
  HeroPlace,
} from './types';
